import { LanguageCode } from 'shared/library/i18n/languages';
import {
  InternalRouteConfig,
  LangMap,
  RouteParamValue,
  RouteQueryValue,
  RouteTabKey,
} from './define-route';
import { routes } from './routes';

export type Route = (typeof routes)[number];
export type RouteName = Route['name'];
export type RouteParams<K extends RouteName> = Parameters<Extract<Route, { name: K }>['build']>[0];
export type InternalRouteTarget = {
  path: string;
  query?: Record<string, RouteQueryValue>;
};

export const routesByName = Object.fromEntries(routes.map((route) => [route.name, route])) as {
  [K in RouteName]: Extract<Route, { name: K }>;
};

export function getInternalRouteConfig<K extends RouteName>(
  name: K,
): InternalRouteConfig<any> | null {
  const internalRoute = routesByName[name].internalRoute;

  if (!internalRoute) {
    return null;
  }

  if (typeof internalRoute === 'string') {
    return {
      path: internalRoute,
    };
  }

  return internalRoute;
}

export function getInternalRoutePath<K extends RouteName>(name: K) {
  return getInternalRouteConfig(name)?.path ?? null;
}

export function hasInternalRoute<K extends RouteName>(name: K) {
  return getInternalRoutePath(name) !== null;
}

export function preservesCurrentTab<K extends RouteName>(name: K) {
  return routesByName[name].preserveCurrentTab === true;
}

export function getRouteTabKey(name: RouteName): RouteTabKey {
  const internalRoutePath = getInternalRoutePath(name);

  if (internalRoutePath === '/inbox') {
    return 'inbox';
  }

  if (
    internalRoutePath === '/selling-a-pet' ||
    internalRoutePath === '/selling-a-cat' ||
    internalRoutePath === '/selling-a-dog' ||
    internalRoutePath === '/listings/create' ||
    internalRoutePath === '/breeders/tools' ||
    internalRoutePath?.startsWith('/me/listings')
  ) {
    return 'sell';
  }

  if (
    internalRoutePath === '/listings' ||
    internalRoutePath?.startsWith('/listings/') ||
    internalRoutePath === '/breeders' ||
    internalRoutePath?.startsWith('/breeders/') ||
    internalRoutePath === '/breeds' ||
    internalRoutePath?.startsWith('/breeds/')
  ) {
    return 'listings';
  }

  if (
    internalRoutePath === '/login' ||
    internalRoutePath === '/auth-from-token' ||
    internalRoutePath === '/petpay' ||
    internalRoutePath?.startsWith('/petpay/') ||
    internalRoutePath === '/me' ||
    internalRoutePath?.startsWith('/me/')
  ) {
    return 'me/index';
  }

  return 'index';
}

export function resolveRouteParamValue(value: RouteParamValue, languageCode: LanguageCode) {
  if (typeof value === 'object' && value !== null) {
    return (
      (value as LangMap)[languageCode] ?? (value as LangMap).sv ?? Object.values(value)[0] ?? ''
    );
  }

  return String(value);
}

export function addRouteQuery(url: string, query?: Record<string, RouteQueryValue>) {
  if (!query || Object.keys(query).length === 0) {
    return url;
  }

  const searchParams = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      searchParams.set(key, String(value));
    }
  });

  const queryString = searchParams.toString();

  return queryString ? `${url}?${queryString}` : url;
}

export function buildLocalizedRoutePath<K extends RouteName>(
  name: K,
  params?: RouteParams<K>,
  locale: LanguageCode = 'en',
) {
  return routesByName[name].build(params as any, locale);
}

export function buildInternalRouteTarget<K extends RouteName>(
  name: K,
  params?: RouteParams<K>,
  locale: LanguageCode = 'en',
): InternalRouteTarget | null {
  const config = getInternalRouteConfig(name);

  if (!config) {
    return null;
  }

  let path = config.path;

  if (params) {
    Object.entries(params as Record<string, RouteParamValue>).forEach(([key, value]) => {
      path = path.replace(`[${key}]`, resolveRouteParamValue(value, locale));
    });
  }

  return {
    path,
    query: config.buildQuery?.(params as never, locale),
  };
}
